import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { io, Socket } from "socket.io-client"
import useLearnerAuth from "../auth/learner/useLearnerAuth"
import { getLearnerAccessToken } from "../auth/learner/session"
import { getSubdomain } from "../utils/getSubdomain"
import {
  getStudentNotifications,
  markStudentNotificationRead,
  markAllStudentNotificationsRead,
  type LearnerNotification,
} from "../api/studentNotifications"

interface LearnerNotificationContextValue {
  notifications: LearnerNotification[]
  unreadCount: number
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  removeNotification: (id: string) => void
}

const LearnerNotificationContext = createContext<LearnerNotificationContextValue | undefined>(undefined)

export const useLearnerNotificationContext = () => {
  const context = useContext(LearnerNotificationContext)
  if (!context) {
    throw new Error("useLearnerNotificationContext must be used within LearnerNotificationProvider")
  }
  return context
}

const sortByNewest = (items: LearnerNotification[]) =>
  [...items].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0
    return bTime - aTime
  })

export const LearnerNotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { student, isAuthenticated } = useLearnerAuth()
  const [notifications, setNotifications] = useState<LearnerNotification[]>([])
  const socketRef = useRef<Socket | null>(null)

  useEffect(() => {
    if (!isAuthenticated) return
    getStudentNotifications()
      .then((res) => setNotifications(sortByNewest(res.data.data ?? [])))
      .catch(() => {})
      
    if (typeof window !== "undefined" && "Notification" in window) {
      if (Notification.permission === "default") {
        Notification.requestPermission()
      }
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !student?.email) return

    const socket = io(`${process.env.REACT_APP_BASE_URL}`, {
      query: { email: student.email, subdomain: getSubdomain(), role: "student" },
      auth: { token: getLearnerAccessToken() },
      transports: ["websocket"],
    })

    socket.on("student:notification", (event: LearnerNotification) => {
      setNotifications((prev) =>
        sortByNewest([
          { ...event, is_read: false },
          ...prev.filter((n) => n.id !== event.id),
        ])
      )
      
      // Native browser notification when tab is inactive
      if (
        typeof window !== "undefined" && 
        "Notification" in window && 
        Notification.permission === "granted" && 
        document.hidden
      ) {
        new Notification(event.title, { body: event.message || "You have a new notification." })
      }
    })

    socketRef.current = socket
    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [isAuthenticated, student?.email])

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  const markAsRead = useCallback(async (id: string) => {
    removeNotification(id)
    await markStudentNotificationRead(id).catch(() => {})
  }, [removeNotification])

  const markAllAsRead = useCallback(async () => {
    setNotifications([])
    await markAllStudentNotificationsRead().catch(() => {})
  }, [])

  const unreadCount = notifications.filter((n) => !n.is_read).length

  return (
    <LearnerNotificationContext.Provider
      value={{ notifications, unreadCount, markAsRead, markAllAsRead, removeNotification }}
    >
      {children}
    </LearnerNotificationContext.Provider>
  )
}
